import React from 'react';
import { Sparkles, Zap, Brain, Rocket, Shield, Wand2, TrendingUp, CheckCircle2 } from 'lucide-react';

const NonTechnicalReassurance = () => {
  return (
    <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-2xl p-8 border-2 border-cyan-500/30">
      
      <div className="text-center mb-8">
        <div className="inline-block bg-cyan-600/20 border border-cyan-500 rounded-full px-6 py-3 mb-4">
          <p className="text-cyan-400 font-bold text-lg">
            🤖 "But I'm Not Technical..."
          </p>
        </div>
        <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
          That's Perfectly Fine.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
            Just Like We Can't Fit A New Boiler Ourselves
          </span>
        </h3>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          You're an expert plumber. We're tech experts.<br />
          You focus on plumbing. We handle the tech. That's the deal.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        
        {/* What The AI Can Do */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-center mb-4">
            <Wand2 className="text-cyan-400 mr-3" size={32} />
            <h4 className="text-2xl font-bold text-white">Our AI Builds For You</h4>
          </div>
          
          <div className="space-y-3">
            {[
              "Landing pages & websites - just describe what you want",
              "Sales funnels that actually convert",
              "Email campaigns with perfect timing",
              "Follow-up sequences that close deals",
              "Booking forms & quote requests",
              "Review request automations",
              "Customer communication templates",
              "Everything branded to YOUR business"
            ].map((item, i) => (
              <div key={i} className="flex items-start">
                <CheckCircle2 className="text-cyan-400 mr-2 mt-1 flex-shrink-0" size={18} />
                <span className="text-gray-200 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Why It's Different */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-center mb-4">
            <Brain className="text-purple-400 mr-3" size={32} />
            <h4 className="text-2xl font-bold text-white">This Isn't Generic ChatGPT</h4>
          </div>
          
          <div className="space-y-3">
            {[
              "Trained specifically on YOUR business",
              "Knows YOUR services, prices, and availability",
              "Understands YOUR customer's questions",
              "Uses YOUR tone of voice",
              "Always up-to-date with latest tech",
              "Learns from every interaction",
              "Gets smarter over time",
              "Built for your industry - not generic"
            ].map((item, i) => (
              <div key={i} className="flex items-start">
                <Sparkles className="text-purple-400 mr-2 mt-1 flex-shrink-0" size={18} />
                <span className="text-gray-200 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Key Benefits */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-xl p-6 text-center">
          <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="text-green-400" size={32} />
          </div>
          <h4 className="text-xl font-bold text-white mb-2">No Setup Hassle</h4>
          <p className="text-gray-300 text-sm">
            We do everything for you. You just tell us what you need. We build it. 48 hours and you're live.
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-xl p-6 text-center">
          <div className="bg-blue-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="text-blue-400" size={32} />
          </div>
          <h4 className="text-xl font-bold text-white mb-2">Always Updated</h4>
          <p className="text-gray-300 text-sm">
            New features? Added automatically. Latest AI models? You get them first. Never outdated.
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-6 text-center">
          <div className="bg-purple-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Rocket className="text-purple-400" size={32} />
          </div>
          <h4 className="text-xl font-bold text-white mb-2">Support When You Need It</h4>
          <p className="text-gray-300 text-sm">
            Questions? Our team (actual humans) helps you. Plus your dedicated success manager.
          </p>
        </div>

      </div>

      {/* Real Example */}
      <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border-2 border-cyan-500 rounded-xl p-6 mb-8">
        <h4 className="text-2xl font-bold text-white mb-4 text-center">
          Here's How Simple It Is:
        </h4>
        
        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-cyan-500">
            <p className="text-cyan-400 font-bold mb-1">You Say:</p>
            <p className="text-gray-200 italic">
              "I need a landing page for my emergency plumbing service with a booking form"
            </p>
          </div>

          <div className="text-center">
            <div className="inline-block bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-bold">
              ⬇️ 15 MINUTES LATER ⬇️
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-lg p-4 border-l-4 border-green-500">
            <p className="text-green-400 font-bold mb-1">Our AI Delivers:</p>
            <p className="text-gray-200">
              ✅ Professional landing page live<br />
              ✅ Booking form connected to your calendar<br />
              ✅ Automatic confirmation texts<br />
              ✅ Follow-up sequence ready<br />
              ✅ Mobile-optimized<br />
              ✅ Branded with your logo & colors
            </p>
          </div>
        </div>

        <p className="text-center text-white font-bold text-lg mt-6">
          No coding. No design work. No headaches.<br />
          <span className="text-cyan-400">Just describe what you want. We make it happen.</span>
        </p>
      </div>

      {/* Testimonial-Style with Photo */}
      <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <img 
              src="https://randomuser.me/api/portraits/men/32.jpg" 
              alt="Mark Stevens"
              className="w-20 h-20 rounded-full object-cover border-4 border-blue-500/30"
            />
          </div>
          <div>
            <p className="text-white text-lg font-bold mb-2">
              "I told them what I needed. They built everything."
            </p>
            <p className="text-gray-300 text-sm mb-4">
              "My calls get answered automatically, quotes go out whilst I'm working, and customers book themselves. 
              I don't touch any of it. It just works. My wife said it's like I got a new team overnight, but I didn't 
              hire anyone. I'm home for dinner now. I see my kids. I honestly don't know how I ran my business before 
              this. I'll never go back to the old way."
            </p>
            <div className="flex items-center space-x-2">
              <p className="text-gray-400 text-sm font-bold">
                Mark, Plumber
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-gray-400 text-sm">Birmingham</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="mt-8 bg-gradient-to-r from-cyan-600/30 to-blue-600/30 rounded-xl p-6 border border-cyan-500/30 text-center">
        <div className="flex items-center justify-center mb-4">
          <TrendingUp className="text-cyan-400 mr-3" size={32} />
          <h4 className="text-2xl font-bold text-white">See The Difference Inside</h4>
        </div>
        <p className="text-xl text-gray-200 mb-4">
          Stop worrying about tech. Stop paying expensive developers.<br />
          <strong className="text-white">Let our AI build everything you need.</strong>
        </p>
        <p className="text-cyan-400 font-bold text-lg">
          Start your £5 trial and see why customers say:<br />
          <span className="text-2xl text-white">"I'll never go back to the old way."</span>
        </p>
      </div>

    </div>
  );
};

export default NonTechnicalReassurance;
